import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client/react';
import cn from 'clsx';
import style from './style.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import { ExtendedCompleteRiskFields } from '@pages-clean/risk-assessment/risk-log/constants';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import { useTranslation } from '@hooks/index';
import { ShowError } from '@utils/notification';
import { deleteRisk, deleteRiskVariables } from '@queries/generated/risk-assessment.generated';
import { DELETE_RISK, GET_RISK_OBSERVATIONS_AND_ACTIONS } from '@queries/risk-assessment';

export interface RiskDeleteDialogProps {
    onClose: () => void;
    onSubmit: () => void;
    risk: ExtendedCompleteRiskFields;
}

export type FormInputs = {
    deleteReason: string | null;
};

const DeleteRiskDialog = ({ onClose, onSubmit, risk }: RiskDeleteDialogProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.deleteRisk' });
    const { t: tCommon } = useTranslation('common');
    const [deleteRiskMutation] = useMutation<deleteRisk, deleteRiskVariables>(DELETE_RISK);
    const { current } = useContext(RiskLogContext);
    const { study } = current;
    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            deleteReason: null,
        },
    });

    const onDelete = async (data: FormInputs) => {
        deleteRiskMutation({
            variables: {
                id: risk.id,
                reason: data.deleteReason,
            },
            refetchQueries: [
                {
                    query: GET_RISK_OBSERVATIONS_AND_ACTIONS,
                    variables: {
                        studyId: study?.id,
                        riskId: current.selectionState.selectedItem?.id,
                    },
                },
            ],
        })
            .then((response) => {
                if (!response?.data || !response?.data?.deleteRisk) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                onSubmit();
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const deletedActionsCount = (risk.observations ?? [])
        .flatMap((obs) => obs?.actions ?? [])
        .filter((action) => action?.deleted === true).length;

    const evaluationHistoryCount = risk.evaluationHistory?.length ?? 0;
    const controlsCount = risk.controls?.length ?? 0;
    const deletedObservationsCount = risk.numDeletedObservations ?? 0;
    const linkedCMControlsCount = risk.numLinkedCMControls ?? 0;

    const associatedDataWarningMessage = (
        <>
            <div className={cn('k-text-left k-mb-1', style.fontSize)}>{t('associatedDataWarningMessage')}</div>
            {evaluationHistoryCount > 0 && (
                <div className={cn('k-text-left', style.fontSize)}>
                    {t('evaluationsCount', {
                        count: evaluationHistoryCount,
                    })}
                </div>
            )}
            {risk.numActiveCtQs > 0 && (
                <div className={cn('k-text-left', style.fontSize)}>
                    {t('ctQsCount', {
                        count: risk.numActiveCtQs,
                    })}
                </div>
            )}
            {controlsCount > 0 && (
                <div className={cn('k-text-left', style.fontSize)}>
                    {t('controlsCount', {
                        count: controlsCount,
                    })}
                </div>
            )}
            {risk.numOpenObservations > 0 && (
                <div className={cn('k-text-left', style.fontSize)}>
                    {t('observationsCount', {
                        count: risk.numOpenObservations,
                    })}
                </div>
            )}
            {deletedObservationsCount > 0 && (
                <div className={cn('k-text-left', style.fontSize)}>
                    {t('deletedObservationsCount', {
                        count: deletedObservationsCount,
                    })}
                </div>
            )}
            {risk.numOpenActions > 0 && (
                <div className={cn('k-text-left', style.fontSize)}>
                    {t('actionsCount', {
                        count: risk.numOpenActions,
                    })}
                </div>
            )}
            {deletedActionsCount > 0 && (
                <div className={cn('k-text-left', style.fontSize)}>
                    {t('deletedActionsCount', {
                        count: deletedActionsCount,
                    })}
                </div>
            )}
            {linkedCMControlsCount > 0 && (
                <div className={cn('k-text-left', style.fontSize)}>
                    {t('deletedLinkedCMControls', {
                        count: linkedCMControlsCount,
                    })}
                </div>
            )}
            {risk.commentCounts.all > 0 && (
                <div className={cn('k-text-left', style.fontSize)}>
                    {t('commentsCount', {
                        count: risk.commentCounts.all,
                    })}
                </div>
            )}

            <div className={cn('k-text-left  k-mt-1', style.fontSize)}>{t('footer')}</div>
        </>
    );

    const riskCanNotBeDeleted = <span className={cn('k-text-left', style.fontSize)}>{t('riskCanNotBeDeleted')}</span>;

    const showCounts =
        evaluationHistoryCount > 0 ||
        controlsCount ||
        deletedObservationsCount ||
        linkedCMControlsCount > 0 ||
        deletedActionsCount > 0 ||
        risk.numActiveCtQs > 0 ||
        risk.numOpenObservations > 0 ||
        risk.numOpenActions > 0 ||
        risk.commentCounts.all > 0;

    return (
        <DraggableDialog
            dataTestId="delete-risk-warning"
            onSubmit={risk.canBeDeleted ? () => handleSubmit(onDelete)() : onClose}
            onClose={onClose}
            title={`${t('title')}: ${risk.recordId}`}
            initialWidth={risk.canBeDeleted ? 600 : 475}
            initialHeight={280}
            height={null}
            primaryBtnLabel={risk.canBeDeleted ? tCommon('delete') : tCommon('close')}
            primaryBtnDisabled={risk.canBeDeleted && (!isValid || !isDirty || isSubmitting)}
            secondaryBtnLabel={risk.canBeDeleted ? undefined : ''}
        >
            <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                {!risk.canBeDeleted && <InfoField type="Warning" message={riskCanNotBeDeleted} />}

                {risk.canBeDeleted && (
                    <>
                        {showCounts && <InfoField type="Warning" message={associatedDataWarningMessage} />}
                        <TextAreaController
                            className={style.textAreaOverrides}
                            maxLength={3000}
                            control={control}
                            name="deleteReason"
                            required
                            data-testid="field-risk-delete-reason"
                            label={`${t('deleteReasonLabel')} *`}
                        />
                    </>
                )}
            </div>
        </DraggableDialog>
    );
};

export default DeleteRiskDialog;
